import type { Crop } from '@protector/entities/crop/models';
import type { MethodologyEntity, MethodologyOrderedInspectionGroupsDTO } from '@protector/entities/methodology/models';
import type {
  FieldLatestScoutingScore,
  ScoutingScoreConfiguration,
  ScoutingScorePerField
} from '@protector/entities/scouting-score/models';
import type { Season } from '@protector/entities/season/models';
import type { Dictionary } from '@protector/utils/types';
import { getNameByCurrentLanguage } from 'core/utils/functions';

export const buildFieldScoutingScoreMap = (fieldsLatestScoutingScore: FieldLatestScoutingScore[] | undefined): ScoutingScorePerField => {
  if (!fieldsLatestScoutingScore) {
    return {};
  }
  const fieldsLatestScoutingScoreDictionary: ScoutingScorePerField = {};
  fieldsLatestScoutingScore.forEach(latestFieldScore => {
    fieldsLatestScoutingScoreDictionary[latestFieldScore.field_id] = {
      score: latestFieldScore.score,
      scoutersScores: latestFieldScore.scores
    };
  });

  return fieldsLatestScoutingScoreDictionary;
};
export const getConfigurationInspectionsGroupsNames = (
  methodologiesBySeason?: MethodologyEntity[],
  configuration?: ScoutingScoreConfiguration
): string => {
  if (methodologiesBySeason && configuration?.inspection_group_ids) {
    const inspectionsGroups = methodologiesBySeason
      .filter(methodology => {
        return methodology.inspection_layout.ordered_inspection_groups.some(inspectionGroup => {
          return configuration.inspection_group_ids?.includes(inspectionGroup.id);
        });
      })
      .flatMap(methodology => {
        return methodology.inspection_layout.ordered_inspection_groups
          .filter(inspectionGroup => {
            return configuration.inspection_group_ids?.includes(inspectionGroup.id);
          })
          .map(inspectionsGroup => getNameByCurrentLanguage(inspectionsGroup.name));
      });
    return inspectionsGroups.join(', ');
  }
  return '--';
};
export const getConfigrationPhenologicalStagesNames = (
  configuration: ScoutingScoreConfiguration,
  crop: Crop | undefined,
  separator: string
): string => {
  if (crop && configuration.development_phases) {
    const { start, end } = configuration.development_phases;
    const startPhase = crop.development_phases?.find(phase => phase.id === start);
    const endPhase = crop.development_phases?.find(phase => phase.id === end);
    if (startPhase && endPhase) {
      if (configuration.development_phases.start === configuration.development_phases.end) {
        return getNameByCurrentLanguage(startPhase.name);
      }
      return `${getNameByCurrentLanguage(startPhase.name)} ${separator} ${getNameByCurrentLanguage(endPhase.name)}`;
    }
  }
  return '--';
};

export const groupInspectionsGroupsBySeason = (
  seasons: Season[],
  methodologies: MethodologyEntity[] | undefined
): Record<string, MethodologyOrderedInspectionGroupsDTO[]> => {
  if (methodologies && seasons) {
    return seasons.reduce<Dictionary<MethodologyOrderedInspectionGroupsDTO[]>>((seasonInspectionGroup, season) => {
      const methodology = methodologies.find(m => m.crop_id === season.crop.id);
      if (!methodology) {
        return seasonInspectionGroup;
      }
      const inspectionsGroups = methodology.inspection_layout.ordered_inspection_groups;
      return {
        ...seasonInspectionGroup,
        [season.id]: [...(seasonInspectionGroup[season.id] || []), ...inspectionsGroups]
      };
    }, {});
  }
  return {};
};
